/**
 * Kachel-Gating: Welche Antrags-Kacheln sind für den aktuellen ZÜP-Status
 * sinnvoll aktiv — und in welchem Zeitraum?
 *
 * Reine Logik ohne React/IO, damit sie testbar bleibt. Datenbasis ist der
 * abgeleitete `zuep_status` aus `GET /api/me` (Gateway, member_status.py).
 *
 * Zeitfenster Verlängerung: aktiv ab RENEWAL_WINDOW_DAYS (60) Tage vor
 * ZÜP-Ablauf. Die eigentliche Freigabe an die Behörde macht die Verwaltung
 * ~4 Wochen vor Ablauf (28-40-Tage-Regel) — die 60 Tage geben dem Mitglied
 * Vorlauf, Daten zu aktualisieren und ggf. ein Strafregister zu besorgen.
 *
 * Flughafenausweis-Daten (monatliche Liste der Ausweisstelle) sind noch
 * nicht importiert — bis dahin bleibt die Ausweis-Kachel bei gültiger ZÜP
 * aktiv, mit dem Hinweis, das Ablaufdatum selbst zu prüfen.
 */

import { ZuepStatusInfo } from './ApiClient';

export const RENEWAL_WINDOW_DAYS = 60;

export interface TileState {
  enabled: boolean;
  /** Zeitraum-Info, z.B. "Aktiv ab 24.03.2027" — wird auf der Kachel angezeigt. */
  zeitraum?: string;
  /** Begründung/Hinweis, v.a. wenn deaktiviert. */
  hinweis?: string;
}

export interface TileStates {
  verlaengerung: TileState;
  komplett: TileState;
  ausweis: TileState;
}

function fmt(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function parseFrist(frist?: string | null): Date | null {
  if (!frist) return null;
  const d = new Date(`${frist}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

export function computeTileStates(
  zs: ZuepStatusInfo | null | undefined,
  opts: {
    isAdmin?: boolean;
    unlinked?: boolean;
    auslandsWohnsitz?: boolean;
    /** Flughafenausweis "gültig bis" (YYYY-MM-DD) aus der Ausweisstellen-Liste. */
    ausweisGueltigBis?: string | null;
    /** Datenstand der Ausweisstellen-Liste (YYYY-MM-DD) — nicht tagesaktuell. */
    ausweisStand?: string | null;
    now?: Date;
  } = {},
): TileStates {
  const now = opts.now ?? new Date();

  const states = computeMemberStates(zs, opts.unlinked === true, now);

  // Echte Flughafenausweis-Daten vorhanden -> Ausweis-Kachel präzise gaten
  // (statt des generischen "bitte selbst prüfen"-Hinweises).
  if (states.ausweis.enabled && opts.ausweisGueltigBis) {
    const bis = parseFrist(opts.ausweisGueltigBis);
    if (bis) {
      const activeFrom = new Date(bis.getTime() - RENEWAL_WINDOW_DAYS * 86_400_000);
      const standNote = opts.ausweisStand
        ? ` (Ausweisdaten: Stand ${fmt(parseFrist(opts.ausweisStand) ?? now)} — nicht tagesaktuell)`
        : '';
      if (now >= activeFrom) {
        states.ausweis = {
          enabled: true,
          zeitraum: `Aktiv seit ${fmt(activeFrom)} — dein Ausweis läuft am ${fmt(bis)} ab${standNote}`,
        };
      } else {
        states.ausweis = {
          enabled: false,
          zeitraum: `Aktiv ab ${fmt(activeFrom)} (${RENEWAL_WINDOW_DAYS} Tage vor Ablauf)`,
          hinweis: `Dein Flughafenausweis ist gültig bis ${fmt(bis)}${standNote}.`,
        };
      }
    }
  }

  // Auslands-Wohnsitz: an der Verlängerungs-Kachel früh auf das
  // Strafregister hinweisen (Beschaffung braucht Vorlauf, <6 Monate alt).
  if (opts.auslandsWohnsitz && zs?.code === 'zuverlaessig') {
    const sr = 'Denk an die Strafregisterbescheinigung aus deinem Auslands-Wohnsitzland (bei Einreichung max. 6 Monate alt).';
    states.verlaengerung = {
      ...states.verlaengerung,
      hinweis: states.verlaengerung.hinweis ? `${states.verlaengerung.hinweis} ${sr}` : sr,
    };
  }

  // Admins sehen alle Kacheln aktiv (Testbarkeit), behalten aber die
  // Zeitraum-Infos — mit sichtbarem Übersteuerungs-Hinweis.
  if (opts.isAdmin) {
    const unlock = (t: TileState): TileState => ({
      ...t,
      enabled: true,
      hinweis: t.enabled ? t.hinweis : `Admin-Modus (für Mitglieder: ${t.hinweis || 'deaktiviert'})`,
    });
    return {
      verlaengerung: unlock(states.verlaengerung),
      komplett: unlock(states.komplett),
      ausweis: unlock(states.ausweis),
    };
  }
  return states;
}

function computeMemberStates(
  zs: ZuepStatusInfo | null | undefined,
  unlinked: boolean,
  now: Date,
): TileStates {
  // Kein verknüpfter Datensatz: vermutlich Neumitglied -> Komplett-Antrag.
  if (unlinked || !zs) {
    return {
      verlaengerung: {
        enabled: false,
        hinweis: 'Kein ZÜP-Datensatz verknüpft — für die Erst-Beantragung den Komplett-Antrag nutzen.',
      },
      komplett: {
        enabled: true,
        zeitraum: 'Für Neumitglieder jederzeit möglich',
      },
      ausweis: {
        enabled: false,
        hinweis: 'Der Flughafenausweis setzt eine abgeschlossene ZÜP voraus.',
      },
    };
  }

  switch (zs.code) {
    case 'zuverlaessig': {
      const frist = parseFrist(zs.frist);
      if (!frist) {
        return {
          verlaengerung: { enabled: false, hinweis: 'Kein Ablaufdatum bekannt — bitte bei der Vereinsverwaltung melden.' },
          komplett: { enabled: false, hinweis: 'Du hast eine gültige ZÜP.' },
          ausweis: { enabled: true, hinweis: 'Ablaufdatum deines Flughafenausweises bitte selbst prüfen.' },
        };
      }
      const activeFrom = new Date(frist.getTime() - RENEWAL_WINDOW_DAYS * 86_400_000);
      const inWindow = now >= activeFrom;
      return {
        verlaengerung: inWindow
          ? {
              enabled: true,
              zeitraum: `Aktiv seit ${fmt(activeFrom)} — deine ZÜP läuft am ${fmt(frist)} ab`,
            }
          : {
              enabled: false,
              zeitraum: `Aktiv ab ${fmt(activeFrom)} (${RENEWAL_WINDOW_DAYS} Tage vor Ablauf)`,
              hinweis: `Deine ZÜP ist noch bis ${fmt(frist)} gültig — aktuell nichts zu tun.`,
            },
        komplett: {
          enabled: false,
          hinweis: 'Du hast eine gültige ZÜP — bei Ablauf genügt die Verlängerung.',
        },
        ausweis: {
          enabled: true,
          hinweis: 'Ablaufdatum deines Flughafenausweises bitte selbst prüfen.',
        },
      };
    }

    case 'in_bearbeitung':
      // Ausweis-Kachel bleibt AKTIV: Der Flughafen braucht Ausweisantrag +
      // Schulungs-Anmeldung ggf. PARALLEL zur laufenden ZÜP — ohne sie
      // übernimmt er den ZÜP-Antrag teils gar nicht (Fall Margreiter 07/26,
      // Ausweis seit 2024 abgelaufen, Übernahme hing 2 Wochen).
      return {
        verlaengerung: { enabled: false, hinweis: 'Dein Antrag läuft bereits bei der Behörde — nichts zu tun.' },
        komplett: { enabled: false, hinweis: 'Dein Antrag läuft bereits bei der Behörde — nichts zu tun.' },
        ausweis: {
          enabled: true,
          hinweis: 'Deine ZÜP läuft gerade — den Flughafenausweis-Antrag kannst du parallel stellen und direkt an die Ausweisstelle senden.',
        },
      };

    case 'offen':
      return {
        verlaengerung: {
          enabled: true,
          hinweis: 'Deine Unterlagen sind unvollständig — bitte ergänze die fehlenden Angaben.',
        },
        komplett: { enabled: false, hinweis: 'Es gibt bereits einen ZÜP-Datensatz — bitte dort ergänzen.' },
        ausweis: { enabled: false, hinweis: 'Erst möglich, wenn deine ZÜP vollständig und abgeschlossen ist.' },
      };

    case 'keine':
      // Zwei Unterfälle: (a) gar kein Datensatz (unlinked) -> Erstantrag über
      // Komplett. (b) Datensatz VORHANDEN, aber keine aktive Frist -- z.B.
      // nach Berechtigungsgeber-Wechsel mit abgelaufener ZÜP (Fall Anich)
      // oder lange abgelaufen: dann ist die VERLÄNGERUNG der richtige Weg
      // (vorbefüllt, ohne Flughafenausweis-Teil).
      if (!unlinked) {
        return {
          verlaengerung: {
            enabled: true,
            zeitraum: 'Jetzt möglich',
            hinweis: 'Keine aktive ZÜP — Daten prüfen/aktualisieren und absenden, wir stellen deine ZÜP über uns (wieder) her.',
          },
          komplett: { enabled: false, hinweis: 'Dein Datensatz existiert bereits — bitte die ZÜP-Verlängerung nutzen.' },
          ausweis: { enabled: false, hinweis: 'Der Flughafenausweis setzt eine abgeschlossene ZÜP voraus.' },
        };
      }
      return {
        verlaengerung: { enabled: false, hinweis: 'Keine aktive ZÜP — für die Erst-Beantragung den Komplett-Antrag nutzen.' },
        komplett: { enabled: true, zeitraum: 'Erstantrag — jederzeit möglich' },
        ausweis: { enabled: false, hinweis: 'Der Flughafenausweis setzt eine abgeschlossene ZÜP voraus.' },
      };

    // angehalten / unzuverlaessig / aufgehoben: Sonderfälle, die das
    // Mitglied nicht selbst lösen kann.
    default:
      return {
        verlaengerung: { enabled: false, hinweis: 'Bitte kontaktiere die Vereinsverwaltung.' },
        komplett: { enabled: false, hinweis: 'Bitte kontaktiere die Vereinsverwaltung.' },
        ausweis: { enabled: false, hinweis: 'Bitte kontaktiere die Vereinsverwaltung.' },
      };
  }
}
